/**
 * OnmetaWidgetPage - React wrapper for the OnMeta SDK widget
 *
 * Loads the OnMeta SDK dynamically and initializes the payment widget.
 * Reads configuration from URL query parameters (set by backend createWidgetUrl).
 *
 * Flow:
 * 1. Backend generates URL: /onmeta-widget?apiKey=...&chainId=...&successRedirectUrl=...
 * 2. This page loads and injects the OnMeta SDK script
 * 3. SDK renders the payment widget in the #onmeta-widget div
 * 4. On ORDER_COMPLETED_EVENTS → notify parent tab + redirect to success page
 * 5. On ORDER_FAILED_EVENTS → notify parent tab + redirect to failure page
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const OnmetaWidgetPage = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const widgetRef = useRef(null);
  const initializedRef = useRef(false);

  // Parse config from URL query params
  const config = useRef({
    apiKey: searchParams.get('apiKey') || '',
    environment: searchParams.get('environment') || 'staging',
    chainId: searchParams.get('chainId') || '137',
    fiatType: searchParams.get('fiatType') || 'inr',
    fiatAmount: searchParams.get('fiatAmount') ? Number(searchParams.get('fiatAmount')) : undefined,
    walletAddress: searchParams.get('walletAddress') || '',
    tokenAddress: searchParams.get('tokenAddress') || undefined,
    tokenSymbol: searchParams.get('tokenSymbol') || undefined,
    userEmail: searchParams.get('userEmail') || undefined,
    paymentMethod: searchParams.get('paymentMethod') || undefined,
    successRedirectUrl: searchParams.get('successRedirectUrl') || undefined,
    failureRedirectUrl: searchParams.get('failureRedirectUrl') || undefined,
    orderId: searchParams.get('orderId') || undefined,
    sessionId: searchParams.get('sessionId') || undefined,
  }).current;

  // Track if we already handled completion to avoid double-redirect
  const completionHandledRef = useRef(false);

  // Notify parent/opener window + BroadcastChannel + localStorage
  const notifyParent = useCallback((message) => {
    // postMessage for opener/parent frames (iframe embedding)
    if (window.opener) window.opener.postMessage(message, '*');
    if (window.parent !== window) window.parent.postMessage(message, '*');

    // BroadcastChannel for cross-tab communication (PaymentPage listens on this)
    if (message.type === 'PAYMENT_STATUS') {
      try {
        const channel = new BroadcastChannel('kuberpay-payment');
        channel.postMessage({
          ...message,
          orderId: config.orderId,
          timestamp: Date.now(),
        });
        channel.close();
      } catch (err) {
        console.warn('BroadcastChannel not available:', err);
      }

      // localStorage fallback (Safari and other browsers)
      try {
        localStorage.setItem('kuberpay-payment-status', JSON.stringify({
          ...message,
          orderId: config.orderId,
          timestamp: Date.now(),
        }));
      } catch (err) {
        console.warn('localStorage not available:', err);
      }
    }
  }, [config.orderId]);

  // Handle completion from any source — redirect this tab to success page
  const handleCompletion = useCallback((source) => {
    if (completionHandledRef.current) return;
    completionHandledRef.current = true;
    console.log(`[OnMeta Widget] Payment completed (source: ${source})`);

    notifyParent({
      type: 'PAYMENT_STATUS',
      status: 'completed',
      provider: 'onmeta',
    });

    if (config.successRedirectUrl) {
      setTimeout(() => {
        window.location.href = config.successRedirectUrl;
      }, 1500);
    }
  }, [config.successRedirectUrl, notifyParent]);

  // Handle failure from any source
  const handleFailure = useCallback((source) => {
    if (completionHandledRef.current) return;
    completionHandledRef.current = true;
    console.log(`[OnMeta Widget] Payment failed (source: ${source})`);

    notifyParent({
      type: 'PAYMENT_STATUS',
      status: 'failed',
      provider: 'onmeta',
    });

    if (config.failureRedirectUrl) {
      setTimeout(() => {
        window.location.href = config.failureRedirectUrl;
      }, 1500);
    }
  }, [config.failureRedirectUrl, notifyParent]);

  // Listen for BroadcastChannel — if success page loads inside SDK iframe,
  // it broadcasts via BroadcastChannel and we catch it here to redirect main window
  useEffect(() => {
    let bcChannel;
    try {
      bcChannel = new BroadcastChannel('kuberpay-payment');
      bcChannel.onmessage = (event) => {
        const data = event.data;
        if (data?.type === 'PAYMENT_STATUS') {
          if (data.status === 'COMPLETED' || data.status === 'completed') {
            handleCompletion('BroadcastChannel');
          } else if (data.status === 'FAILED' || data.status === 'failed') {
            handleFailure('BroadcastChannel');
          }
        }
      };
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
    }
    return () => { if (bcChannel) bcChannel.close(); };
  }, [handleCompletion, handleFailure]);

  // Listen for postMessage from OnMeta SDK iframe
  useEffect(() => {
    const handleMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      // OnMeta SDK might post various message formats
      if (data.type === 'ORDER_COMPLETED' ||
          data.eventType === 'ORDER_COMPLETED' ||
          data.event === 'ORDER_COMPLETED') {
        handleCompletion('postMessage');
      } else if (data.type === 'ORDER_FAILED' ||
          data.eventType === 'ORDER_FAILED' ||
          data.event === 'ORDER_FAILED') {
        handleFailure('postMessage');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleCompletion, handleFailure]);

  // Show fallback button after 2 minutes (user may have completed payment externally)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFallbackButton(true);
    }, 2 * 60 * 1000); // 2 minutes
    return () => clearTimeout(timer);
  }, []);

  // Initialize widget after SDK loads
  const initializeWidget = useCallback(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    try {
      setLoading(false);

      // Build widget config matching OnMeta SDK params
      const widgetConfig = {
        elementId: 'onmeta-widget',
        apiKey: config.apiKey,
        environment: config.environment,
        walletAddress: config.walletAddress,
        onRamp: 'enabled',
        widgetHeight: '600px',
        widgetWidth: '100%',
      };

      // Optional params — only set if provided
      if (config.fiatType) widgetConfig.fiatType = config.fiatType;
      if (config.fiatAmount) widgetConfig.fiatAmount = config.fiatAmount;
      if (config.chainId) widgetConfig.chainId = String(config.chainId);
      if (config.tokenAddress) widgetConfig.tokenAddress = config.tokenAddress;
      if (config.tokenSymbol) widgetConfig.tokenSymbol = config.tokenSymbol;
      if (config.userEmail) widgetConfig.userEmail = config.userEmail;
      if (config.paymentMethod) widgetConfig.paymentMethod = config.paymentMethod;
      if (config.successRedirectUrl) widgetConfig.successRedirectUrl = config.successRedirectUrl;
      if (config.failureRedirectUrl) widgetConfig.failureRedirectUrl = config.failureRedirectUrl;

      // Build metadata — must be JSON-stringified because OnMeta SDK
      // stores the value as submeta1 using .toString(). Passing an object
      // produces "[object Object]" and our orderId is lost.
      // By stringifying, submeta1 = '{"orderId":"cm12345"}' which our
      // backend can parse back to recover the orderId for order matching.
      const metaData = {};
      if (config.orderId) metaData.orderId = config.orderId;
      if (config.sessionId) metaData.sessionId = config.sessionId;
      if (Object.keys(metaData).length > 0) {
        widgetConfig.metaData = JSON.stringify(metaData);
      }

      // Initialize the OnMeta widget
      // eslint-disable-next-line no-undef
      const widget = new onMetaWidget(widgetConfig);
      widget.init();
      widgetRef.current = widget;

      // Relay all events to parent window (for iframe embedding)
      // Show fallback button once user progresses past KYC (payment initiated)
      widget.on('ALL_EVENTS', (data) => {
        console.log('[OnMeta Widget Event]', data);
        notifyParent({ type: 'ONMETA_EVENT', payload: data });

        // Show fallback button when payment-related events fire
        const eventType = data?.type || data?.eventType || data?.event || '';
        const paymentEvents = ['ORDER_CREATED', 'PAYMENT_INITIATED', 'PAYMENT_PENDING',
          'ORDER_PROCESSING', 'TRANSACTION_INITIATED', 'TRANSACTION_CREATED'];
        if (paymentEvents.some(e => eventType.toUpperCase().includes(e))) {
          setShowFallbackButton(true);
        }
      });

      // Handle order completion via SDK event
      widget.on('ORDER_COMPLETED_EVENTS', (data) => {
        console.log('[OnMeta Order Completed]', data);
        handleCompletion('SDK_EVENT');
      });

      // Handle order failure via SDK event
      widget.on('ORDER_FAILED_EVENTS', (data) => {
        console.log('[OnMeta Order Failed]', data);
        handleFailure('SDK_EVENT');
      });
    } catch (err) {
      console.error('Failed to initialize OnMeta widget:', err);
      setError('Failed to initialize payment widget: ' + err.message);
    }
  }, [config, notifyParent, handleCompletion, handleFailure]);

  // Load OnMeta SDK script
  useEffect(() => {
    if (!config.apiKey) {
      setLoading(false);
      setError('Missing API key. Please try again.');
      return;
    }

    const sdkUrl =
      config.environment === 'production'
        ? 'https://platform.onmeta.in/onmeta-sdk.js'
        : 'https://stg.platform.onmeta.in/onmeta-sdk.js';

    // Check if already loaded (e.g., from a previous render)
    if (window.onMetaWidget) {
      initializeWidget();
      return;
    }

    const script = document.createElement('script');
    script.src = sdkUrl;
    script.async = true;
    script.onload = () => initializeWidget();
    script.onerror = () => {
      setLoading(false);
      setError('Failed to load OnMeta SDK. Please check your network and try again.');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script tag on unmount
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config.apiKey, config.environment, initializeWidget]);

  return (
    <div className="onmeta-page">
      <div className="onmeta-page-container">
        {/* Header bar */}
        <div className="onmeta-header">
          <div className="onmeta-header-logo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="5" width="20" height="14" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
            <span>Secure Payment</span>
          </div>
          <div className="onmeta-header-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>Protected</span>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="onmeta-loading-card">
            <div className="onmeta-loading-spinner" />
            <p className="onmeta-loading-text">Initializing payment widget...</p>
            <p className="onmeta-loading-subtext">Please wait while we set things up</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="onmeta-error-card">
            <div className="onmeta-error-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="onmeta-error-title">Something went wrong</h3>
            <p className="onmeta-error-message">{error}</p>
            <button
              className="onmeta-retry-btn"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Widget container — SDK renders inside here */}
        <div
          id="onmeta-widget"
          className="onmeta-widget-container"
          style={{ display: loading || error ? 'none' : 'block' }}
        />

        {/* Fallback link — shown after 2 min or when payment events detected */}
        {!loading && !error && showFallbackButton && (
          <div className="onmeta-footer-actions">
            <button
              className="onmeta-completed-btn"
              onClick={() => handleCompletion('manual')}
            >
              Payment completed? Click here to continue
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

// Inject styles (stylesheet for animations + layout)
const injectStyles = () => {
  const existing = document.getElementById('onmeta-widget-styles');
  if (existing) return;

  const style = document.createElement('style');
  style.id = 'onmeta-widget-styles';
  style.textContent = `
    /* ========== PAGE LAYOUT ========== */
    .onmeta-page {
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .onmeta-page-container {
      width: 100%;
      max-width: 440px;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
      border-radius: 16px;
      overflow: visible;
    }

    /* ========== HEADER BAR ========== */
    .onmeta-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 14px 20px;
      background: rgba(255, 255, 255, 0.97);
      backdrop-filter: blur(10px);
      border-radius: 16px 16px 0 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    .onmeta-header-logo {
      display: flex;
      align-items: center;
      gap: 8px;
      color: #7c3aed;
      font-weight: 600;
      font-size: 15px;
    }

    .onmeta-header-badge {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 500;
      color: #059669;
      background: #ecfdf5;
      padding: 4px 10px;
      border-radius: 20px;
    }

    /* ========== WIDGET CONTAINER ========== */
    .onmeta-widget-container {
      width: 100%;
      min-height: 600px;
      background: #ffffff;
      border-radius: 0 0 16px 16px;
      overflow: hidden;
    }

    /* Let the SDK iframe/content fill the container properly */
    .onmeta-widget-container iframe {
      width: 100% !important;
      min-height: 600px;
      border: none !important;
      display: block;
    }

    /* ========== LOADING STATE ========== */
    .onmeta-loading-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 500px;
      background: white;
      padding: 40px 24px;
      border-radius: 0 0 16px 16px;
    }

    .onmeta-loading-spinner {
      width: 48px;
      height: 48px;
      border: 4px solid #e5e7eb;
      border-top-color: #7c3aed;
      border-radius: 50%;
      animation: onmeta-spin 0.8s linear infinite;
      margin-bottom: 20px;
    }

    .onmeta-loading-text {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 6px;
    }

    .onmeta-loading-subtext {
      font-size: 13px;
      color: #9ca3af;
      margin: 0;
    }

    @keyframes onmeta-spin {
      to { transform: rotate(360deg); }
    }

    /* ========== ERROR STATE ========== */
    .onmeta-error-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 400px;
      background: white;
      padding: 40px 24px;
      text-align: center;
      border-radius: 0 0 16px 16px;
    }

    .onmeta-error-icon {
      color: #ef4444;
      margin-bottom: 16px;
    }

    .onmeta-error-title {
      font-size: 20px;
      font-weight: 600;
      color: #1a1a1a;
      margin: 0 0 10px;
    }

    .onmeta-error-message {
      font-size: 14px;
      color: #6b7280;
      margin: 0 0 24px;
      line-height: 1.5;
      max-width: 320px;
    }

    .onmeta-retry-btn {
      padding: 12px 32px;
      background: linear-gradient(135deg, #7c3aed 0%, #6366f1 100%);
      color: white;
      border: none;
      border-radius: 10px;
      font-size: 15px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
    }

    .onmeta-retry-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(124, 58, 237, 0.3);
    }

    /* ========== FOOTER ACTIONS ========== */
    .onmeta-footer-actions {
      display: flex;
      justify-content: center;
      padding: 12px 20px;
      background: rgba(255, 255, 255, 0.97);
      border-top: 1px solid rgba(0, 0, 0, 0.06);
    }

    .onmeta-completed-btn {
      background: none;
      border: none;
      color: #7c3aed;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      text-decoration: underline;
      padding: 8px 16px;
    }

    .onmeta-completed-btn:hover {
      color: #6366f1;
    }

    /* ========== RESPONSIVE ========== */
    @media (max-width: 480px) {
      .onmeta-page {
        padding: 0;
        justify-content: flex-start;
      }

      .onmeta-page-container {
        max-width: 100%;
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        border-radius: 0;
        box-shadow: none;
      }

      .onmeta-header {
        border-radius: 0;
      }

      .onmeta-widget-container {
        flex: 1;
      }

      .onmeta-loading-card,
      .onmeta-error-card {
        flex: 1;
      }
    }
  `;
  document.head.appendChild(style);
};

injectStyles();

export default OnmetaWidgetPage;
